import React from 'react';
import PropTypes from 'prop-types';
import { CheckCircle } from '@mui/icons-material';
import { Box, Typography, CircularProgress, Chip, Button, Stack } from '@mui/material';
import PgHomeSectionPaper from './PgHomeSectionPaper';
import { formatRelativeTime } from '../../../globals/utils/homeHelpers';

/** Shared pending-indemnity bond list for PG and Law Firm home dashboards. */
export default function HomeIndemnityListSection({
  copy,
  bonds,
  loadingIndemnity,
  markingBondId,
  onMarkClick,
  resolveBondUrl,
  paperSx,
  formatRelativeTimeFn,
}) {
  return (
    <PgHomeSectionPaper
      sx={{
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        ...paperSx,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: '#333333' }}>
              {copy.title}
            </Typography>
            <Chip size="small" color="warning" label={bonds.length} />
          </Box>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
            {copy.subtitle}
          </Typography>
        </Box>
        {loadingIndemnity && <CircularProgress size={18} />}
      </Stack>
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, minHeight: 0, maxHeight: 320 }}>
        {bonds.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {copy.emptyList}
          </Typography>
        ) : (
          bonds.map((bond) => (
            <Box
              key={bond.bond_id}
              sx={{
                p: 1,
                mb: 1,
                borderRadius: 1,
                bgcolor: (theme) => (theme.palette.mode === 'light' ? '#ffffff' : 'background.default'),
                border: '1px solid rgba(37, 99, 235, 0.12)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', noWrap: true, mb: 1 }}>
                  {bond.bond_number ? `${bond.full_name || '—'} - ${bond.bond_number}` : bond.full_name || '—'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {bond.bond_type && `${bond.bond_type}`}
                  {bond.bond_type && bond.law_firm_name && ' • '}
                  {bond.law_firm_name && `${bond.law_firm_name}`}
                  {bond.attorney_name && ` • ${bond.attorney_name}`}
                  {bond.created_at &&
                    ` • ${(formatRelativeTimeFn ?? formatRelativeTime)(bond.created_at)}`}
                </Typography>
              </Box>
              <Box display="flex" gap={0.5}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const url = resolveBondUrl(bond);
                    if (url) window.open(url, '_blank');
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.5 }}
                >
                  {copy.view}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={
                    markingBondId === bond.bond_id ? (
                      <CircularProgress size={14} />
                    ) : (
                      <CheckCircle sx={{ fontSize: 14 }} />
                    )
                  }
                  onClick={() => onMarkClick(bond)}
                  disabled={markingBondId === bond.bond_id}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.5 }}
                >
                  {copy.markCompleted}
                </Button>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </PgHomeSectionPaper>
  );
}

HomeIndemnityListSection.propTypes = {
  copy: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    emptyList: PropTypes.string.isRequired,
    view: PropTypes.string.isRequired,
    markCompleted: PropTypes.string.isRequired,
  }).isRequired,
  bonds: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadingIndemnity: PropTypes.bool.isRequired,
  markingBondId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onMarkClick: PropTypes.func.isRequired,
  resolveBondUrl: PropTypes.func.isRequired,
  paperSx: PropTypes.object,
  formatRelativeTimeFn: PropTypes.func,
};

HomeIndemnityListSection.defaultProps = {
  markingBondId: null,
  paperSx: {},
  formatRelativeTimeFn: null,
};
